import { AxiosPromise } from 'axios';

import { AssignmentMessage } from '../../../../types/AssignmentMessage';
import { AssignmentRequestMessage } from '../../../../types/AssignmentRequestMessage';
import { CreateCustomerRequestMessage } from '../../../../types/CreateCustomerRequestMessage';
import { CreateSupplierRequestMessage } from '../../../../types/CreateSupplierRequestMessage';
import { CustomerCompanyMessage } from '../../../../types/CustomerCompanyMessage';
import { CustomerGroupSettingsMessage } from '../../../../types/CustomerGroupSettingsMessage';
import { ProductContext } from '../../../../types/ProductContext';
import { SupplierCompanyMessage } from '../../../../types/SupplierCompanyMessage';
import { SupplierGroupMessage } from '../../../../types/SupplierGroupMessage';
import { SupplierGroupSettingsMessage } from '../../../../types/SupplierGroupSettingsMessage';
import { SupplierSettingsMessage } from '../../../../types/SupplierSettingsMessage';
import { authorizedAxiosInstance } from '../instances/AuthorizedAxiosInstance';
import { nonEmpty } from '../paramUtils';

export interface GetCustomersParams {
    searchQuery?: string;
    ids?: string[] | string;
    groupId?: string;
}

export const getCustomers = async ({ searchQuery, ids, groupId }: GetCustomersParams = {}): Promise<
    CustomerCompanyMessage[]
> => {
    const response = await authorizedAxiosInstance.get('/v1/customer/list', {
        params: {
            searchQuery: searchQuery?.trim() || undefined,
            ids: nonEmpty(ids),
            groupId: nonEmpty(groupId),
            types: ProductContext.LUBES,
        },
    });

    return response.data;
};

export interface GetCustomerGroupsParams {
    searchQuery?: string;
    ids?: string[] | string;
}

export const getCustomerGroups = async ({ searchQuery, ids }: GetCustomerGroupsParams): Promise<any> => {
    const response = await authorizedAxiosInstance.get('/v1/customergroup/list', {
        params: {
            searchQuery: searchQuery?.trim() || undefined,
            ids: nonEmpty(ids),
        },
    });

    return response.data;
};

export interface GetSupplierGroupsParams {
    searchQuery?: string;
    ids?: string[] | string;
}

export const getSupplierGroups = async ({
    searchQuery,
    ids,
}: GetSupplierGroupsParams): Promise<SupplierGroupMessage[]> => {
    const response = await authorizedAxiosInstance.get('/v1/suppliergroup/list', {
        params: {
            searchQuery: searchQuery?.trim() || undefined,
            ids: nonEmpty(ids),
        },
    });

    return response.data;
};

export interface GetSuppliersParams {
    searchQuery?: string;
    ids?: string[] | string;
    groupId?: string;
}

export const getSuppliers = async ({ searchQuery, ids, groupId }: GetSuppliersParams = {}): Promise<
    SupplierCompanyMessage[]
> => {
    const response = await authorizedAxiosInstance.get('/v1/supplier/list', {
        params: {
            searchQuery: searchQuery?.trim() || undefined,
            ids: nonEmpty(ids),
            groupId: nonEmpty(groupId),
            types: ProductContext.LUBES,
        },
    });

    return response.data;
};

export const getCustomerDetails = async (id: string): Promise<CustomerCompanyMessage> => {
    const response = await authorizedAxiosInstance.get(`/v1/customer/${id}`);

    return response.data;
};

interface UpdateCustomerDetailsParams {
    id: string;
    customerData: unknown;
}

export const updateCustomerDetails = async ({
    id,
    customerData,
}: UpdateCustomerDetailsParams): Promise<CustomerCompanyMessage> => {
    const response = await authorizedAxiosInstance.put(`/v1/customer/${id}`, customerData);

    return response.data;
};

export const getSupplierDetails = async (id: string): Promise<SupplierCompanyMessage> => {
    const response = await authorizedAxiosInstance.get(`/v1/supplier/${id}`);

    return response.data;
};

interface UpdateSupplierDetailsParams {
    id: string;
    supplierData: SupplierCompanyMessage;
}

export const updateSupplierDetails = async ({
    id,
    supplierData,
}: UpdateSupplierDetailsParams): Promise<SupplierCompanyMessage> => {
    const response = await authorizedAxiosInstance.put(`/v1/supplier/${id}`, supplierData);

    return response.data;
};

export const getSupplierSettings = async (id: string): Promise<SupplierSettingsMessage> => {
    const response = await authorizedAxiosInstance.get(`/v1/suppliers/${id}/settings`);

    return response.data;
};

export const updateSupplierSettings = async ({
    id,
    settings,
}: {
    id: string;
    settings: SupplierSettingsMessage;
}): Promise<SupplierSettingsMessage> => {
    const response = await authorizedAxiosInstance.put(`/v1/suppliers/${id}/settings`, settings);

    return response.data;
};

export const createCustomer = async (
    customerData: Omit<CreateCustomerRequestMessage, 'type'>
): Promise<CustomerCompanyMessage> => {
    const response = await authorizedAxiosInstance.post(`/v1/customer`, {
        ...customerData,
        type: ProductContext.LUBES,
    });

    return response.data;
};

export const createSupplier = async (
    supplierData: Omit<CreateSupplierRequestMessage, 'type'>
): Promise<SupplierCompanyMessage> => {
    const response = await authorizedAxiosInstance.post(`/v1/supplier`, {
        ...supplierData,
        type: ProductContext.LUBES,
    });

    return response.data;
};

export const createAssignment = async (assignment: AssignmentRequestMessage): Promise<AssignmentMessage> => {
    const response = await authorizedAxiosInstance.post('/v1/assignment', assignment);

    return response.data;
};

export const updateAssignment = (
    assignmentId: string,
    assignment: AssignmentMessage
): AxiosPromise<AssignmentMessage> => {
    return authorizedAxiosInstance.put(`/v1/assignment/${assignmentId}`, assignment);
};

export const deleteAssignment = (assignmentId: string): AxiosPromise<void> => {
    return authorizedAxiosInstance.delete(`/v1/assignment/${assignmentId}`);
};

export const getCustomerGroupSettings = async (id: string): Promise<CustomerGroupSettingsMessage> => {
    const response = await authorizedAxiosInstance.get(`/v2/customer-groups/${id}/settings`);
    return response.data;
};

export const getSupplierGroupSettings = async (id: string): Promise<SupplierGroupSettingsMessage> => {
    const response = await authorizedAxiosInstance.get(`/v2/supplier-groups/${id}/settings`);
    return response.data;
};

export const updateCustomerGroupSettings = async ({
    groupId,
    settings,
}: {
    groupId: string;
    settings: CustomerGroupSettingsMessage;
}): Promise<CustomerGroupSettingsMessage> => {
    const response = await authorizedAxiosInstance.put(`/v2/customer-groups/${groupId}/settings`, settings);
    return response.data;
};

export const updateSupplierGroupSettings = async ({
    groupId,
    settings,
}: {
    groupId: string;
    settings: SupplierGroupSettingsMessage;
}): Promise<SupplierGroupSettingsMessage> => {
    const response = await authorizedAxiosInstance.put(`/v2/supplier-groups/${groupId}/settings`, settings);
    return response.data;
};
